<template>
  <h1>{{ tn("shipping") }}</h1>
  <div v-if="props.showCheckBox" class="checkbox mb-double">
    <input id="checkboxUseBillingAddress" v-model="buyFlowStore.shippingMatchesBilling" type="checkbox" />
    <label for="checkboxUseBillingAddress">{{ tn("sameAddressLabel") }}</label>
  </div>
  <ul v-if="!buyFlowStore.shippingMatchesBilling" class="form tight">
    <li>
      <label for="shippingFirstName">{{ tn("firstName") }}</label>
      <InputErrorComponent :show="showErrors.firstName">
        <input id="shippingFirstName" v-model="buyFlowStore.shippingAddress.firstName" type="text" />
        <template #error>
          <div>
            <span id="showFirstNameError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li>
      <label for="shippingLastName">{{ tn("lastName") }}</label>
      <InputErrorComponent :show="showErrors.lastName">
        <input id="shippingLastName" v-model="buyFlowStore.shippingAddress.lastName" type="text" />
        <template #error>
          <div>
            <span id="showLastNameError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li>
      <label for="shippingCountry">{{ tn("country") }}</label>
      <InputErrorComponent :show="showErrors.country">
        <select
          id="shippingCountry"
          v-model="buyFlowStore.shippingAddress.country"
          class="select-css mr-half mb-half-mobile"
        >
          <option v-for="country in countriesList" :key="country.countryName" :value="country.countryName">
            {{ country.countryName }}
          </option>
        </select>
        <template #error>
          <div>
            <span id="showCountryError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li v-if="statesAndProvinces.length > 0">
      <label for="shippingState">{{ tn("state") }}</label>
      <InputErrorComponent :show="showErrors.state">
        <select
          id="shippingState"
          v-model="buyFlowStore.shippingAddress.state"
          class="select-css mr-half mb-half-mobile"
        >
          <option key="selectOne" value="">{{ tn("selectOne") }}</option>
          <option v-for="item in statesAndProvinces" :key="item.value" :value="item.text">{{ item.text }}</option>
        </select>
        <template #error>
          <div>
            <span id="showStateError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li v-else>
      <label for="shippingState">{{ tn("state") }}</label>
      <InputErrorComponent :show="showErrors.state">
        <input id="shippingState" v-model="buyFlowStore.shippingAddress.state" type="text" />
        <template #error>
          <div>
            <span id="showStateError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li>
      <label for="shippingAddress">{{ tn("address") }}</label>
      <InputErrorComponent :show="showErrors.address1">
        <input id="shippingAddress" v-model="buyFlowStore.shippingAddress.address1" type="text" />
        <template #error>
          <div>
            <span id="showAddress1Error">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li>
      <label for="shippingAddress2">{{ tn("address2") }}</label>
      <input id="shippingAddress2" v-model="buyFlowStore.shippingAddress.address2" type="text" />
    </li>

    <li>
      <label for="shippingCity">{{ tn("city") }}</label>
      <InputErrorComponent :show="showErrors.city">
        <input id="shippingCity" v-model="buyFlowStore.shippingAddress.city" type="text" />
        <template #error>
          <div>
            <span id="showCityError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>

    <li class="pb">
      <label for="shippingZip">{{ tn("zip") }}</label>
      <InputErrorComponent :show="showErrors.zipCode">
        <input id="shippingZip" v-model="buyFlowStore.shippingAddress.zipCode" type="text" />
        <template #error>
          <div>
            <span id="showZipError">{{ tn("requiredFieldError") }}</span>
          </div>
        </template>
      </InputErrorComponent>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { unifiedApi } from "@/common";
import { ICountry } from "@/common/api/unifiedPortal/interfaces";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { computed, onBeforeMount, ref, watch } from "vue";
import InputErrorComponent from "@/components/shared/InputError.vue";
import { shippingStatesAndProvinces, IStateAndProvinceItem } from "@/common/ShippingStatesAndProvinces";

const componentName = "ShippingForm";
const buyFlowStore = useBuyFlowStore();
const countriesList = ref<ICountry[] | null>();
const showErrors = computed(() => {
  return buyFlowStore.showShippingFormErrors;
});

const statesAndProvinces = computed<IStateAndProvinceItem[]>(() => {
  const values = shippingStatesAndProvinces[buyFlowStore.shippingAddress.country];

  if (values && values.length > 0) {
    return values;
  }
  return [];
});

const props = defineProps({
  showCheckBox: {
    type: Boolean,
    default: true,
  },
});

logEvent("created", componentName);

onBeforeMount(async () => {
  const res = (await unifiedApi.getCountries()).data;
  countriesList.value = res?.countries;
  buyFlowStore.shippingAddress.country = countriesList?.value?.find(x => x.countryCode === "US")?.countryName ?? "";
});

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}

watch(
  () => buyFlowStore.$state.shippingAddress.country,
  () => {
    //clear the state when the country changes
    buyFlowStore.shippingAddress.state = "";
  }
);
</script>
